import React, { useState } from "react";
import { AlertCircle } from "lucide-react";
import { usePostMediaMutation } from "../../api/mediaApi.ts";
import { useNavigate } from "react-router";
import { useAuth } from "react-oidc-context";
import { ImageUploadWithCrop } from "../../components/media/ImageUploadWithCrop.tsx";
import { Trans } from "@lingui/react/macro";
import InfoBox from "../../components/util/InfoBox.tsx";
import { t } from "@lingui/core/macro";

const MediaUpload: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [description, setDescription] = useState("");
  const {
    mutate: uploadMedia,
    isPending,
    isError,
    error,
  } = usePostMediaMutation();
  const [imageBlob, setImageBlob] = useState<Blob>();
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string>();

  const handleImageChange = (blob: Blob | undefined) => {
    // Clean up old URL
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
    }

    setImageBlob(blob);

    if (blob) {
      const url = URL.createObjectURL(blob);
      setImagePreviewUrl(url);
    } else {
      setImagePreviewUrl(undefined);
    }
  };

  const handleImageUpload = () => {
    if (imageBlob) {
      const mediaCreationDto = {
        description: description,
        media: imageBlob,
      };
      uploadMedia(mediaCreationDto, {
        onSuccess: async () => {
          // Clean up preview
          if (imagePreviewUrl) {
            URL.revokeObjectURL(imagePreviewUrl);
            setImagePreviewUrl(undefined);
          }
          setImageBlob(undefined);
          navigate(`/profile/${user?.profile.sub}`);
        },
        onError: () => {
          console.error("Error while uploading profile picture");
        },
      });
    }
  };

  return (
    <div className="mx-auto w-full max-w-2xl p-6">
      <div className="rounded-lg bg-white p-8 shadow-lg">
        <h2 className="mb-6 text-2xl font-bold text-gray-900">
          <Trans>Upload Media</Trans>
        </h2>
        <InfoBox variant={"info"} className={"mb-4"}>
          <Trans>
            Currently you can only upload images. Videos coming soon...
          </Trans>
        </InfoBox>
        <div className="space-y-6">
          <div>
            <ImageUploadWithCrop
              onImageChange={handleImageChange}
              showCropper={false}
              image={imagePreviewUrl}
              isUploading={isPending}
              maxSizeBytes={800_000}
              renderPreview={(imageUrl) =>
                imageUrl && (
                  <img
                    alt={"Upload media"}
                    className={"h-full w-full"}
                    src={imageUrl}
                  />
                )
              }
            />
          </div>

          <div>
            <label
              htmlFor="description"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              <Trans>Description</Trans>
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder={t`Add a caption for your image...`}
              className="w-full resize-none rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {isError && (
            <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4">
              <AlertCircle className="h-5 w-5 flex-shrink-0 text-red-500" />
              <p className="text-sm text-red-700">
                {error instanceof Error
                  ? error.message
                  : t`Failed to upload media. Please try again.`}
              </p>
            </div>
          )}

          <button
            onClick={handleImageUpload}
            disabled={!imageBlob || isPending}
            className="bg-soluud-primary hover:bg-soluud-primary-hover mb-4 w-full cursor-pointer rounded-xl px-4 py-2 text-white transition"
          >
            {isPending ? t`Uploading...` : t`Upload Media`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MediaUpload;
